import Sprite = PIXI.Sprite;
import Point = PIXI.Point;
import Text = PIXI.Text;
import Container = PIXI.Container;
import DisplayObject = PIXI.DisplayObject;
import ColorMatrixFilter = PIXI.filters.ColorMatrixFilter;
import Graphics = PIXI.Graphics;
import SCALE_MODES = PIXI.SCALE_MODES;
import Texture = PIXI.Texture;
import { Button } from '../UI/elements/buttons/Button';
import { LivesCounter } from '../UI/elements/counters/LivesCounter';
import { CoinsCounter } from '../UI/elements/counters/CoinsCounter';
import { SceneManager } from '../core/managers/SceneManager';
import { MessagePopUp, MessagePopUpParams } from '../popUps/MessagePopUp';
import { PopUpManager, PopUpName } from '../core/managers/PopUpManager';
import { SettingState, SettingsManager } from '../core/managers/SettingsManager';
import { GameAnalytics } from '../analytics/GameAnalytics';
import { Donut } from '../core/gameplay/Donut';
import { animateMessage } from './Animations';
import { TextStyles } from './TextStyles';
import { Translator } from '../core/managers/Translator';

let vibrating: boolean = false;

export function translate(key: string, upper: boolean = false): string {
    let result: string = Translator.getInstance().getTranslation(key);
    if (upper) {
        result = result.toUpperCase();
    }
    return result;
}

export function createSprite(textureName: string, parent?: Container): Sprite {
    if (!endsWith(textureName, '.png') && !endsWith(textureName, '.jpg')) {
        textureName = textureName + '.png';
    }
    const sprite: Sprite = Sprite.from(textureName);
    if (endsWith(textureName, '@2x.png')) {
        sprite.scale.set(0.5, 0.5);
    }
    if (parent) {
        parent.addChild(sprite);
    }
    return sprite;
}

export function createDonut(textureName: string, parent?: Container): Donut {
    const donut: Donut = createSprite(textureName, parent) as Donut;
    donut.color = textureName;
    return donut;
}

export function createButton(
    defaultSkin: string,
    parent?: Container,
    callback?: () => any,
    label?: string,
    defaultAnimations: boolean = true,
    appearAnimation: boolean = true,
    scale: number = 0.5
): Button {
    const button: Button = new Button(defaultSkin, callback, label, defaultAnimations, appearAnimation, scale);
    if (parent) {
        parent.addChild(button);
    }
    return button;
}

export function desaturate(object: DisplayObject): void {
    const colorMatrix: ColorMatrixFilter = new PIXI.filters.ColorMatrixFilter();
    object.filters = [colorMatrix];
    colorMatrix.desaturate();
}

export function resaturate(object: DisplayObject): void {
    object.filters = [];
}

export function endsWith(str: string, suffix: string): boolean {
    return str.indexOf(suffix, str.length - suffix.length) !== -1;
}

export function getRandomColor(): number {
    return Math.random() * 0xFFFFFF;
}

export function getDateFromTimeStamp(): string {
    const date: Date = new Date();
    const day: number = date.getDate();
    const month: number = date.getMonth() + 1;
    return '' + day + '.' + month + '.' + date.getFullYear();
}

export function addCounters(parent: Container, posLives?: Point, posCoins?: Point): void {
    const livesCounter: LivesCounter = new LivesCounter(parent);
    const coinsCounter: CoinsCounter = new CoinsCounter(parent);
    const centerX: number = SceneManager.gameWidth / 2;
    if (!posLives) {
        posLives = new Point(204, 20);
    }
    if (!posCoins) {
        posCoins = new Point(342, 20);
    }

    livesCounter.position = posLives;
    coinsCounter.position = posCoins;
}

export function equalizeLabels(label1: Text, label2: Text): void {
    const scale1: number = label1.scale.x;
    const scale2: number = label2.scale.x;
    let smallest: number = scale1;
    smallest = scale2 <= smallest ? scale2 : scale1;
    label1.scale.set(smallest, smallest);
    label2.scale.set(smallest, smallest);
}

export function secondsToHMS(totalSeconds: number, short: boolean = false): string {
    let hoursString: string = ' hours, ';
    let minsString: string = ' minutes, ';
    let secsString: string = ' seconds';
    if (short) {
        hoursString = 'h ';
        minsString = 'm ';
        secsString = 's';
    }
    const hours: number = Math.floor(totalSeconds / 3600);
    const minutes: number = Math.floor((totalSeconds - hours * 3600) / 60);
    const seconds: number = totalSeconds - hours * 3600 - minutes * 60;
    let timeHMS: string = '';
    if (hours > 0) {
        timeHMS += hours + hoursString;
    }
    if (minutes > 0) {
        timeHMS += minutes + minsString;
    }
    if (seconds > 0) {
        timeHMS += seconds + secsString;
    }
    return timeHMS;
}

export function restrictLabel(space: Sprite | Graphics, text: Text): void {
    while (text.width > space.width || text.height > space.height) {
        text.scale.set(text.scale.x * 0.9, text.scale.x * 0.9);
    }
}

export function getMePlayer(): Player {
    return {
        id: FBInstant.player.getID(),
        name: FBInstant.player.getName(),
        photo: FBInstant.player.getPhoto()
    };
}

export function showMessagePopUp(message: string, hideTimeout?: number, keepQueue: boolean = true, onCloseCallback?: () => any): void {
    const params: MessagePopUpParams = {
        message: message,
        hideTimeout: hideTimeout,
    };
    if (onCloseCallback) {
        params.onCloseCallback = onCloseCallback;
    }
    const popUp: MessagePopUp = PopUpManager.getInstance().getView(PopUpName.MessagePopUp, params, true) as MessagePopUp;
    popUp.show(keepQueue);
}

export function vibrate(duration: number): void {
    if (SettingsManager.getInstance().getSettingsVibrate() === SettingState.On) {
        if (!vibrating) {
            vibrating = true;
            if (window.navigator && window.navigator.vibrate) {
                window.navigator.vibrate(duration);
            }
            setTimeout((): void => {
                vibrating = false;
            }, duration);
        }
    }
}

export function generateTexture(container: Container): Texture {
    const text: Texture = SceneManager.getInstance()._app.renderer.generateTexture(container, SCALE_MODES.LINEAR, 2);
    return text;
}

declare type ObjectWithWidth = Container | Sprite | Graphics | Text;

export function setScaleByReference(target: ObjectWithWidth, reference: ObjectWithWidth, byWidth: boolean = true): void {
    let targetScale: number;
    if (byWidth) {
        targetScale = reference.width / target.width;
    }
    if (!byWidth) {
        targetScale = reference.height / target.height;
    }
    target.scale.set(targetScale, targetScale);
}

export function resizeTextByReference(target: Text, reference: ObjectWithWidth, margin: number = 5): void {
    if (target.width > 3 && reference.width > 3 && target.height > 3 && reference.height > 3) {
        while (target.width > reference.width - margin || target.height > reference.height - margin) {
            let fontSize: number = parseFloat(target.style.fontSize);
            fontSize -= 0.5;
            target.style.fontSize = fontSize + 'px';
            target.style.lineHeight = fontSize;
            if (target.style.strokeThickness) {
                target.style.strokeThickness = (fontSize / 10).toFixed(1);
                if (target.style.strokeThickness < 2.5) {
                    target.style.strokeThickness = 2.5;
                }
            }
            target.anchor.set(0, 0);
        }
    }
}

export type PlayerID = string;

export interface Player {
    id: PlayerID;
    name: string;
    photo: string;
}

export function delayPromise(delay: number): Promise<any> {
    return new Promise((resolve: any): any => {
        setTimeout(resolve, delay);
    });
}

export function showSorryNoAd(): void {
    const message: Text = new Text('Sorry, no ad available', TextStyles.messageText);
    message.anchor.set(0.5, 0.5);
    message.position.set(SceneManager.gameWidth / 2, SceneManager.gameHeight / 2 + 80);
    SceneManager.getInstance().stage.addChild(message);
    animateMessage(message, 1.2, 0.8);
}

export function showAdCanceled(): void {
    const message: Text = new Text('Ad canceled by user', TextStyles.messageText);
    message.anchor.set(0.5, 0.5);
    message.position.set(SceneManager.gameWidth / 2, SceneManager.gameHeight / 2 + 80);
    SceneManager.getInstance().stage.addChild(message);
    animateMessage(message, 1.2, 0.8);
}

export function isIphone(): boolean {
    return PIXI.utils.isMobile && !PIXI.utils.isMobile.tablet && PIXI.utils.isMobile.apple.phone;
}